import { onDocumentReady } from '../utils/events';

/**
 * Count key figures into place when they scroll into view.
 *
 * Values are authored in ACF as display strings ("150+", "480 M€", "1er"), so
 * the number is parsed out of the string and any prefix/suffix is preserved
 * verbatim. Ordinals and values without a number are left alone.
 */

const SELECTORS = [
  '.text-w-stats__statistic-value',
  '.key-figures__stat-value',
];

const DURATION = 1600;

// Leading text, the number, then trailing text. A group separator only counts
// when a digit follows it, so the space in "480 M€" stays with the unit rather
// than being swallowed by the number.
const VALUE_PATTERN = /^(\D*?)(\d(?:[\s\u202F\u00A0]?\d)*)(.*)$/s;

/**
 * Where the count starts.
 *
 * Counting up from zero reads as a slot machine on big numbers, so start at the
 * round number below instead: 150 counts from 100, 480 from 400, 35 from 30.
 * Small numeric values count up from zero.
 */
const startValue = (target) => {
  if (target <= 5) return 0;

  const step = Math.pow(10, String(Math.floor(target)).length - 1);

  return Math.floor((target - 1) / step) * step;
};

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

// Ordinals describe a rank rather than a quantity, so they do not count.
const ORDINAL = /^(er|ère|re|e|ᵉ|st|nd|rd|th)$/i;

const format = (n, prefix, suffix, decimals) =>
  `${prefix}${n.toFixed(decimals).replace('.', ',')}${suffix}`;

const animate = ({ el, prefix, from, target, suffix }) => {
  const decimals = (String(target).split('.')[1] || '').length;
  const start = performance.now();

  const tick = (now) => {
    const progress = Math.min(1, (now - start) / DURATION);
    const done = progress === 1;
    const current = done ? target : from + (target - from) * easeOut(progress);

    el.textContent = format(current, prefix, suffix, decimals);

    if (!done) requestAnimationFrame(tick);
  };

  el.textContent = format(from, prefix, suffix, decimals);
  requestAnimationFrame(tick);
};

const prepare = (el) => {
  const match = el.textContent.trim().match(VALUE_PATTERN);

  if (!match) return null;

  const [, prefix, digits, suffix] = match;
  if (ORDINAL.test(suffix.trim())) return null;

  // Group separators are presentational, so drop them before parsing.
  const target = parseFloat(digits.replace(/[\s\u202F\u00A0]/g, ''));

  if (!isFinite(target)) return null;

  return { el, prefix, target, suffix, from: startValue(target) };
};

onDocumentReady(() => {
  const nodes = document.querySelectorAll(SELECTORS.join(', '));

  if (!nodes.length) return;

  const stats = Array.from(nodes).map(prepare).filter(Boolean);

  if (!stats.length) return;

  // Respect a reduced-motion preference by leaving the final values in place.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // No IntersectionObserver (or the figures are already past): leave them be
  // rather than risk showing a start value that never animates.
  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const stat = stats.find((s) => s.el === entry.target);

        observer.unobserve(entry.target);

        if (stat) animate(stat);
      });
    },
    { threshold: 0.6 }
  );

  stats.forEach((stat) => {
    // Hold the start value so the jump happens off-screen, not in view.
    stat.el.textContent = `${stat.prefix}${stat.from}${stat.suffix}`;
    observer.observe(stat.el);
  });
});
